import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, type AdminShareBook } from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import type { ColumnDef, RowAction, BulkAction } from "@/components/ui";
import { Lock, Unlock, Eye, Download } from "lucide-react";
import { AdminShareBookDetailModal } from "./AdminShareBookDetailModal";
import { downloadCsv } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

const LOCK_STATUS_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "locked", label: "Låsta" },
  { value: "unlocked", label: "Olåsta" },
];

export function AdminShareBooks() {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isBulkLocking, setIsBulkLocking] = useState(false);
  const [viewShareBookId, setViewShareBookId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    refresh,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  } = useDataTable<AdminShareBook, { lockStatus: string; companyId: string }>({
    fetcher: (params) =>
      adminApi.listShareBooks({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        isLocked: params.lockStatus === "locked" ? true : params.lockStatus === "unlocked" ? false : undefined,
        companyId: params.companyId || undefined,
      }),
    pageSize: 25,
    initialFilters: { lockStatus: "", companyId: "" },
    sortKeyMap: {
      company: "companyName",
      shareCapital: "shareCapital",
      shareholdersCount: "shareholdersCount",
      createdAt: "createdAt",
    },
  });

  const handleLock = useCallback(
    async (shareBook: AdminShareBook) => {
      const action = shareBook.isLocked ? "låsa upp" : "låsa";
      if (!confirm(`Är du säker på att du vill ${action} denna aktiebok?`)) return;
      try {
        const response = await adminApi.updateShareBook(shareBook.id, {
          isLocked: !shareBook.isLocked,
          lockedReason: shareBook.isLocked ? null : "Låst av administratör",
        });
        if (response.success) {
          refresh();
          toast.success(`Aktieboken har ${shareBook.isLocked ? "låsts upp" : "låsts"}`);
        }
      } catch {
        toast.error(`Kunde inte ${action} aktieboken`);
      }
    },
    [refresh]
  );

  const columns: ColumnDef<AdminShareBook>[] = useMemo(
    () => [
      {
        id: "company",
        header: "Företag",
        sortable: true,
        sortAccessor: (row) => row.companyName,
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök namn eller org.nr...",
        },
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-gray-900">{row.companyName}</p>
            {row.organizationNumber && (
              <p className="text-xs text-gray-500">{row.organizationNumber}</p>
            )}
          </div>
        ),
      },
      {
        id: "shareCapital",
        header: "Aktiekapital",
        sortable: true,
        sortAccessor: (row) => row.shareCapital,
        cell: (row) => (
          <span className="text-sm text-gray-600">{formatCurrency(row.shareCapital)}</span>
        ),
      },
      {
        id: "shareholdersCount",
        header: "Aktieägare",
        hideBelow: "sm",
        sortable: true,
        sortAccessor: (row) => row.shareholdersCount,
        cell: (row) => (
          <span className="text-sm text-gray-600">{row.shareholdersCount}</span>
        ),
      },
      {
        id: "transactionsCount",
        header: "Transaktioner",
        hideBelow: "md",
        sortable: false,
        cell: (row) => (
          <span className="text-sm text-gray-500">{row.transactionsCount}</span>
        ),
      },
      {
        id: "clauses",
        header: "Förbehåll",
        hideBelow: "lg",
        cell: (row) => {
          const clauses = [];
          if (row.hasPreemptionClause) clauses.push("Förköp");
          if (row.hasConsentClause) clauses.push("Samtycke");
          if (row.hasRedemptionClause) clauses.push("Hembud");
          if (row.hasConversionClause) clauses.push("Omvandling");

          if (clauses.length === 0) {
            return <span className="text-sm text-gray-400">Inga</span>;
          }

          return (
            <div className="flex flex-wrap gap-1">
              {clauses.map((clause) => (
                <span
                  key={clause}
                  className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600"
                >
                  {clause}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: "lockStatus",
        header: "Status",
        sortable: false,
        filter: {
          type: "select" as const,
          value: filters.lockStatus,
          onChange: (v: string) => setFilter("lockStatus", v),
          options: LOCK_STATUS_OPTIONS,
        },
        cell: (row) => {
          if (row.isLocked) {
            return (
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <Lock size={12} />
                  Låst
                </span>
                {row.lockedReason && (
                  <span className="text-[10px] text-red-600 max-w-[120px] truncate" title={row.lockedReason}>
                    {row.lockedReason}
                  </span>
                )}
              </div>
            );
          }
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <Unlock size={12} />
              Aktiv
            </span>
          );
        },
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
    ],
    [search, setSearch, filters.lockStatus, setFilter]
  );

  const rowActions: RowAction<AdminShareBook>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Visa detaljer",
        icon: Eye,
        onClick: (row) => setViewShareBookId(row.id),
      },
      {
        key: "unlock",
        label: "Lås upp",
        icon: Unlock,
        variant: "success",
        onClick: handleLock,
        visible: (row) => row.isLocked,
      },
      {
        key: "lock",
        label: "Lås",
        icon: Lock,
        variant: "danger",
        onClick: handleLock,
        visible: (row) => !row.isLocked,
      },
    ],
    [handleLock]
  );

  // Bulk actions
  const handleBulkLock = useCallback(
    async (shareBooks: AdminShareBook[], lock: boolean) => {
      const targetBooks = lock
        ? shareBooks.filter((sb) => !sb.isLocked)
        : shareBooks.filter((sb) => sb.isLocked);

      if (targetBooks.length === 0) {
        toast.error(`Inga aktieböcker kan ${lock ? "låsas" : "låsas upp"}`);
        return;
      }

      const action = lock ? "låsa" : "låsa upp";
      if (!confirm(`Är du säker på att du vill ${action} ${targetBooks.length} aktieböcker?`)) {
        return;
      }

      setIsBulkLocking(true);
      try {
        let successCount = 0;
        let errorCount = 0;
        for (const shareBook of targetBooks) {
          try {
            const response = await adminApi.updateShareBook(shareBook.id, {
              isLocked: lock,
              lockedReason: lock ? "Låst av administratör (bulk)" : null,
            });
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch {
            errorCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} aktieböcker har ${lock ? "låsts" : "låsts upp"}`);
          setSelectedKeys(new Set());
          refresh();
        }
        if (errorCount > 0) {
          toast.error(`Kunde inte ${action} ${errorCount} aktieböcker`);
        }
      } finally {
        setIsBulkLocking(false);
      }
    },
    [refresh]
  );

  const handleBulkExport = useCallback((shareBooks: AdminShareBook[]) => {
    downloadCsv(
      shareBooks,
      [
        { header: "ID", accessor: (sb) => sb.id },
        { header: "Företag", accessor: (sb) => sb.companyName },
        { header: "Org.nummer", accessor: (sb) => sb.organizationNumber || "" },
        { header: "Aktiekapital", accessor: (sb) => sb.shareCapital.toString() },
        { header: "Antal aktieägare", accessor: (sb) => sb.shareholdersCount.toString() },
        { header: "Antal transaktioner", accessor: (sb) => sb.transactionsCount.toString() },
        { header: "Förköpsförbehåll", accessor: (sb) => sb.hasPreemptionClause ? "Ja" : "Nej" },
        { header: "Samtyckesförbehåll", accessor: (sb) => sb.hasConsentClause ? "Ja" : "Nej" },
        { header: "Hembudsförbehåll", accessor: (sb) => sb.hasRedemptionClause ? "Ja" : "Nej" },
        { header: "Omvandlingsförbehåll", accessor: (sb) => sb.hasConversionClause ? "Ja" : "Nej" },
        { header: "Status", accessor: (sb) => sb.isLocked ? "Låst" : "Aktiv" },
        { header: "Skapad", accessor: (sb) => new Date(sb.createdAt).toLocaleDateString("sv-SE") },
      ],
      `aktiebocker-urval-${new Date().toISOString().slice(0, 10)}.csv`
    );
    toast.success(`${shareBooks.length} aktieböcker exporterade`);
  }, []);

  const bulkActions: BulkAction<AdminShareBook>[] = useMemo(
    () => [
      {
        key: "export",
        label: "Exportera",
        icon: Download,
        onClick: handleBulkExport,
      },
      {
        key: "lock",
        label: "Lås",
        icon: Lock,
        variant: "danger",
        onClick: (books) => handleBulkLock(books, true),
        loading: isBulkLocking,
      },
      {
        key: "unlock",
        label: "Lås upp",
        icon: Unlock,
        variant: "success",
        onClick: (books) => handleBulkLock(books, false),
        loading: isBulkLocking,
      },
    ],
    [handleBulkExport, handleBulkLock, isBulkLocking]
  );

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.shareBooks?.title || "Aktieböcker - Admin"} noIndex />
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Aktieböcker</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Alla aktieböcker i systemet
            </p>
          </div>
          <Button
            className="w-full sm:w-auto"
            variant="outline"
            size="sm"
            onClick={() =>
              downloadCsv(
                data,
                [
                  { header: "ID", accessor: (sb) => sb.id },
                  { header: "Företag", accessor: (sb) => sb.companyName },
                  { header: "Org.nummer", accessor: (sb) => sb.organizationNumber || "" },
                  { header: "Aktiekapital", accessor: (sb) => sb.shareCapital.toString() },
                  { header: "Antal aktieägare", accessor: (sb) => sb.shareholdersCount.toString() },
                  { header: "Antal transaktioner", accessor: (sb) => sb.transactionsCount.toString() },
                  { header: "Förköpsförbehåll", accessor: (sb) => sb.hasPreemptionClause ? "Ja" : "Nej" },
                  { header: "Samtyckesförbehåll", accessor: (sb) => sb.hasConsentClause ? "Ja" : "Nej" },
                  { header: "Hembudsförbehåll", accessor: (sb) => sb.hasRedemptionClause ? "Ja" : "Nej" },
                  { header: "Omvandlingsförbehåll", accessor: (sb) => sb.hasConversionClause ? "Ja" : "Nej" },
                  { header: "Status", accessor: (sb) => sb.isLocked ? "Låst" : "Aktiv" },
                  { header: "Skapad", accessor: (sb) => new Date(sb.createdAt).toLocaleDateString("sv-SE") },
                ],
                `aktiebocker-${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
            disabled={data.length === 0}
          >
            <Download size={14} />
            Exportera CSV
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={data}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          emptyMessage="Inga aktieböcker hittades"
          pagination={pagination}
          onPageChange={setPage}
          pageSizeOptions={[25, 50, 100, 250]}
          onPageSizeChange={setPageSize}
          rowActions={rowActions}
          rowClassName={(row) => (row.isLocked ? "bg-red-50/50 hover:bg-red-100/50" : undefined)}
          serverSortColumnId={sortColumnId}
          serverSortDirection={sortDirection}
          onSort={handleSort}
          onResetFilters={resetFilters}
          selectable
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          bulkActions={bulkActions}
        />
      </div>

      {/* View Share Book Modal */}
      {viewShareBookId && (
        <AdminShareBookDetailModal
          shareBookId={viewShareBookId}
          onClose={() => setViewShareBookId(null)}
          onUpdated={refresh}
        />
      )}
    </>
  );
}
